import type { CompetitorPage } from "@/data/compare";

// Controlled August 2026 expansion batch. Every factual product or pricing
// statement below is backed by the first-party sources attached to that page.
export const expansionCompetitors: CompetitorPage[] = [
  {
    slug: "jobhire-ai",
    name: "JobHire.AI",
    category: "AI job matching, document tailoring, and optional auto apply",
    title: "LifeShack vs JobHire.AI",
    metaTitle: "LifeShack vs JobHire.AI | Auto Apply Comparison",
    metaDescription:
      "Compare LifeShack and JobHire.AI for AI job matching, resume tailoring, cover letters, auto apply, review controls, tracking, and pricing transparency.",
    officialUrl: "https://jobhire.ai/",
    pricingNote:
      "JobHire.AI offers Standard, Pro, and Extended subscriptions but publishes exact prices after registration; its official FAQ says there is no free trial.",
    bestFor: [
      "Candidates who want matching, resume help, cover letters, and applying in one platform",
      "Job seekers who want to choose between automatic and review-first applications",
      "Applicants comfortable reviewing plan prices after registration",
    ],
    watchouts: [
      "Exact subscription prices are not shown on the public FAQ",
      "There is no free trial according to the official FAQ",
      "The homepage limits the advertised service to U.S. roles",
    ],
    verdict:
      "Choose JobHire.AI for a broad job-search suite with optional review before applications. Choose LifeShack for a focused application pipeline you can test with free credits before selecting a paid plan.",
    competitorSummary:
      "JobHire.AI combines job matching, resume optimization, personalized cover letters, application tracking, and the option to apply automatically or review opportunities first.",
    lifeShackSummary:
      "LifeShack turns job preferences and candidate information into a recurring pipeline of relevant, tailored, submitted, and tracked applications with pause and company-blocking controls.",
    tableRows: [
      {
        label: "Primary workflow",
        competitor: "Use a broad AI job-search suite and choose whether to review roles before applying.",
        lifeShack: "Set preferences and run a focused recurring application pipeline.",
      },
      {
        label: "Application control",
        competitor: "Settings offer instant auto-apply, a 24-hour delay, or manual approval before queueing.",
        lifeShack: "Preferences, pause controls, tracking, and company blocklists.",
      },
      {
        label: "Materials",
        competitor: "Resume optimization and personalized cover letters.",
        lifeShack: "Optional resume and cover-letter tailoring connected to submissions.",
      },
      {
        label: "Starting access",
        competitor: "No free trial; exact plan prices appear after registration.",
        lifeShack: "Free application credits with no credit card required.",
      },
      {
        label: "Best fit",
        competitor: "A broad all-in-one job-search toolkit.",
        lifeShack: "A focused application execution layer.",
      },
    ],
    sections: [
      {
        heading: "How JobHire.AI Works",
        body: [
          "JobHire.AI asks candidates for a resume and search preferences, then matches roles, helps optimize the resume, creates cover letters, and can apply on the candidate's behalf.",
          "Its FAQ describes instant auto-apply, a 24-hour delay, or fully manual approval. In manual mode, candidates approve or reject a proposed application before an approved job enters the submission queue. The homepage advertises U.S. roles only.",
        ],
      },
      {
        heading: "Where LifeShack Differs",
        body: [
          "LifeShack is narrower: the product is organized around getting relevant applications completed and tracked after the search criteria are set.",
          "The practical difference is trialability and focus. LifeShack exposes a free-credit starting path, while JobHire.AI asks users to register before seeing exact subscription prices and does not advertise a free trial.",
        ],
      },
      {
        heading: "Bottom Line",
        body: [
          "Use JobHire.AI if you want a larger toolkit and a choice between review-first and automatic applications.",
          "Use LifeShack if you want to test a focused application agent before paying and keep controls around the recurring submission workflow.",
        ],
      },
    ],
    chooseCompetitor: [
      "You want matching, resume tools, cover letters, and applications in one suite.",
      "You want the option to review opportunities before they are submitted.",
      "You are comfortable registering before comparing exact plan prices.",
    ],
    chooseLifeShack: [
      "You want to test the workflow with free application credits.",
      "You want a product centered on recurring application execution.",
      "You value pause controls, tracking, and company blocklists.",
    ],
    faqs: [
      {
        question: "Does JobHire.AI submit applications automatically?",
        answer:
          "Yes. Its FAQ describes instant auto-apply, a 24-hour delay, or manual approval in User Settings. In manual mode, approving a proposed application puts it into the submission queue; approval is not itself proof of submission.",
      },
      {
        question: "Does JobHire.AI have a free trial?",
        answer:
          "No. Its official FAQ says there is no free trial and that exact plan pricing is shown after registration.",
      },
    ],
    sources: [
      { label: "JobHire.AI official product page", href: "https://jobhire.ai/" },
      { label: "JobHire.AI official FAQ", href: "https://jobhire.ai/faq" },
    ],
  },
  {
    slug: "applyghost",
    name: "ApplyGhost",
    category: "Server-side auto apply for technical and tech-adjacent roles",
    title: "LifeShack vs ApplyGhost",
    metaTitle: "LifeShack vs ApplyGhost | Tech Auto Apply Comparison",
    metaDescription:
      "Compare LifeShack and ApplyGhost for server-side auto apply, tech-role coverage, match thresholds, resume selection, application volume, controls, and pricing.",
    officialUrl: "https://applyghost.com/",
    pricingNote:
      "ApplyGhost lists €29 per month for 10 applications a week, €59 for 30 a week, and €99 for 75 a week, plus 10 free applications without a card.",
    bestFor: [
      "Software, product, design, data, AI, security, and DevOps candidates",
      "Applicants who want server-side submission without a browser extension",
      "Candidates who keep multiple resumes for different technical roles",
    ],
    watchouts: [
      "The product is intentionally focused on technical and tech-adjacent roles",
      "Applications scoring below its published match floor are filtered out",
      "Weekly quotas may not suit candidates who want a short high-volume sprint",
    ],
    verdict:
      "Choose ApplyGhost when a tech-only search, automatic resume selection, and a published match floor fit your needs. Choose LifeShack for a broader role mix and monthly application-credit workflow.",
    competitorSummary:
      "ApplyGhost is a server-side agent for technical roles that matches jobs, selects among tagged resumes, applies to roles meeting its fit threshold, and tracks the resulting pipeline.",
    lifeShackSummary:
      "LifeShack supports a broader preference-led search, optional material tailoring, ongoing submissions, application tracking, pause controls, and company blocklists.",
    tableRows: [
      {
        label: "Primary workflow",
        competitor: "Server-side agent for technical and tech-adjacent roles.",
        lifeShack: "Cloud application agent for a broader range of target roles.",
      },
      {
        label: "Targeting",
        competitor: "Published 70% match floor plus role, seniority, location, salary, and company filters.",
        lifeShack: "Candidate preferences and blocklists guide the search and submissions.",
      },
      {
        label: "Resume workflow",
        competitor: "Selects the best tagged resume for each matching technical role.",
        lifeShack: "Can tailor resumes and cover letters in the application workflow.",
      },
      {
        label: "Application allowance",
        competitor: "Weekly quotas of 10, 30, or 75 on published monthly plans.",
        lifeShack: "Monthly application credits vary by plan.",
      },
      {
        label: "Starting access",
        competitor: "10 free applications without a credit card.",
        lifeShack: "Free application credits without a credit card.",
      },
    ],
    sections: [
      {
        heading: "How ApplyGhost Works",
        body: [
          "ApplyGhost runs server-side rather than through a browser extension. Candidates upload resumes, tag them by target role, set detailed preferences, and let the agent submit jobs that clear its published fit threshold.",
          "The scope is deliberate: its official site names software, product, design, data, AI, security, DevOps, and related technical roles rather than every industry.",
        ],
      },
      {
        heading: "Where LifeShack Differs",
        body: [
          "LifeShack is a closer fit for candidates whose target roles are not confined to the technical categories ApplyGhost names. It also organizes paid usage around monthly application credits instead of weekly quotas.",
          "Both products aim to remove the last-click burden, so the decision should come down to role coverage, targeting controls, application allowance, and how each product handles your actual resume variants.",
        ],
      },
      {
        heading: "Bottom Line",
        body: [
          "Use ApplyGhost for a technical search that benefits from explicit fit scoring and automatic resume selection.",
          "Use LifeShack for broader role coverage and a focused monthly application pipeline.",
        ],
      },
    ],
    chooseCompetitor: [
      "Your search is limited to technical or tech-adjacent roles.",
      "You want a published match threshold before submission.",
      "You want the agent to choose among role-specific resumes.",
    ],
    chooseLifeShack: [
      "Your target roles extend beyond ApplyGhost's stated categories.",
      "You prefer monthly application credits to weekly quotas.",
      "You want pause controls and company blocklists in a focused workflow.",
    ],
    faqs: [
      {
        question: "Does ApplyGhost require a browser extension?",
        answer:
          "No. ApplyGhost says its agent runs server-side and submits applications without an extension or final click.",
      },
      {
        question: "What roles does ApplyGhost cover?",
        answer:
          "Its official site focuses on software, product, design, data, AI, security, DevOps, and other technical or tech-adjacent roles.",
      },
    ],
    sources: [
      { label: "ApplyGhost official product and pricing page", href: "https://applyghost.com/" },
    ],
  },
  {
    slug: "scale-jobs",
    name: "Scale.jobs",
    category: "Human-managed job application service",
    title: "LifeShack vs Scale.jobs",
    metaTitle: "LifeShack vs Scale.jobs | Auto Apply vs Human Service",
    metaDescription:
      "Compare LifeShack and Scale.jobs for AI auto apply versus human-managed applications, turnaround, document support, proof of submission, volume, and pricing.",
    officialUrl: "https://scale.jobs/",
    pricingNote:
      "Scale.jobs lists one-time base packages of $199 for 250 applications, $299 for 500, and $399 for 1,000. Job finding and AI-custom resumes are separate add-ons on these plans; check the selected total, not just the headline price.",
    bestFor: [
      "Candidates who explicitly prefer people to complete applications",
      "Job seekers who want direct communication with an assigned assistant",
      "Applicants willing to pay a larger one-time fee for a defined application package",
    ],
    watchouts: [
      "Human-assistant plans require a larger upfront payment",
      "Applications enter a human-managed service queue rather than an instant-submit flow",
      "Job finding, AI-custom resumes, and career services can increase the base package price",
    ],
    verdict:
      "Choose Scale.jobs when human handling, communication, and proof of work justify the upfront cost. Choose LifeShack when you want a lower-friction software agent you can test before committing.",
    competitorSummary:
      "Scale.jobs assigns human assistants to complete applications and custom cover letters, communicate with candidates, and provide tracked submission evidence. Job finding is an optional paid service on its base application packages.",
    lifeShackSummary:
      "LifeShack uses software automation to find relevant roles, prepare materials when needed, submit supported applications, and track activity with candidate controls.",
    tableRows: [
      {
        label: "Who applies",
        competitor: "A human assistant completes applications.",
        lifeShack: "An AI application agent handles supported workflows.",
      },
      {
        label: "Turnaround",
        competitor: "Human-managed queue with onboarding and service turnaround.",
        lifeShack: "Automated recurring pipeline after profile setup.",
      },
      {
        label: "Proof and tracking",
        competitor: "Dashboard updates, assistant notes, and screenshot documentation.",
        lifeShack: "In-product application activity and status tracking.",
      },
      {
        label: "Pricing model",
        competitor: "One-time base packages from $199 for 250 applications; job finding costs extra.",
        lifeShack: "Free starting credits and subscriptions for higher monthly volume.",
      },
      {
        label: "Best fit",
        competitor: "Human delegation and direct communication.",
        lifeShack: "Software automation and a lighter starting commitment.",
      },
    ],
    sections: [
      {
        heading: "How Scale.jobs Works",
        body: [
          "Scale.jobs is a managed service rather than an auto-apply bot. Its current pricing page assigns one or two human assistants to packages of 250, 500, or 1,000 applications.",
          "Base packages include custom cover letters and applications to jobs you supply. Adding job finding costs $99 on Basic, $149 on Standard, or $199 on Best Value. AI-custom resumes are separately priced add-ons. The $1,099 Ultimate Bundle advertises 1,100 applications and the premium add-ons.",
        ],
      },
      {
        heading: "Where LifeShack Differs",
        body: [
          "LifeShack uses an AI agent instead of assigning a person to the account. Its free-credit starting path lets you test supported applications without buying a human-assistant package, but it does not provide the direct assistant relationship described by Scale.jobs. No head-to-head speed result is established here.",
          "This is less a feature checklist than an operating-model choice: managed human labor with a one-time package versus self-service software with free starting credits and monthly plans.",
        ],
      },
      {
        heading: "Bottom Line",
        body: [
          "Use Scale.jobs when a named human assistant and proof-of-work process are worth the upfront spend.",
          "Use LifeShack when you want to test automated application execution before making a larger commitment.",
        ],
      },
    ],
    chooseCompetitor: [
      "You explicitly want a person completing applications.",
      "You value direct assistant communication and screenshot proof.",
      "You can commit to a one-time application package.",
    ],
    chooseLifeShack: [
      "You prefer software automation to a managed human service.",
      "You want a free starting path without a large upfront purchase.",
      "You want recurring applications with direct product controls.",
    ],
    faqs: [
      {
        question: "Is Scale.jobs an AI auto-apply tool?",
        answer:
          "Its paid application service is human-managed. Scale.jobs says assistants manually complete applications, while optional AI tools support documents and other job-search tasks.",
      },
      {
        question: "How much does Scale.jobs cost?",
        answer:
          "The one-time base packages are $199 for 250 applications, $299 for 500, and $399 for 1,000. Job finding adds $99, $149, or $199 respectively. The observed Best Value selection totaled $598 with job finding added, not $399. Confirm the selected services and final total before buying.",
      },
    ],
    sources: [
      { label: "Scale.jobs official pricing", href: "https://scale.jobs/pricing" },
      { label: "Scale.jobs official FAQ", href: "https://scale.jobs/faq" },
    ],
  },
  {
    slug: "massive",
    name: "Massive",
    category: "Autopilot job matching and application service",
    title: "LifeShack vs Massive",
    metaTitle: "LifeShack vs Massive | AI Auto Apply Comparison",
    metaDescription:
      "Compare LifeShack and Massive for job matching, autopilot applications, custom resumes, visa filters, submission visibility, and pricing clarity.",
    officialUrl: "https://usemassive.com/",
    pricingNote:
      "Massive's public homepage explains its workflow and advertises up to 200 applications a month, but it does not display a public plan price; confirm terms in signup.",
    bestFor: [
      "Candidates who want an autopilot workflow based on Massive's job inventory",
      "Job seekers who want custom resumes and cover letters for each job",
      "Applicants who need a visa-sponsorship company filter",
    ],
    watchouts: [
      "Public plan pricing is not displayed on the homepage",
      "The workflow centers on jobs surfaced through Massive's matching system",
      "Material previews are advertised, but that does not establish an approval gate for every application",
    ],
    verdict:
      "Choose Massive when its curated job matching, visa filters, and up-to-200 monthly workflow fit your search. Choose LifeShack when transparent starting access and direct preference controls matter more.",
    competitorSummary:
      "Massive matches candidates with jobs, can apply automatically or from manually selected matches, creates custom resumes and cover letters, and shows the submitted application materials.",
    lifeShackSummary:
      "LifeShack runs a preference-led application workflow with optional tailoring, submitted-application tracking, pause controls, company blocklists, and free starting credits.",
    tableRows: [
      {
        label: "Primary workflow",
        competitor: "Match jobs from Massive's inventory and apply manually or through Autopilot.",
        lifeShack: "Set preferences and run a recurring application pipeline.",
      },
      {
        label: "Published volume",
        competitor: "Advertises up to 200 applications per month.",
        lifeShack: "Application credits vary by plan.",
      },
      {
        label: "Materials",
        competitor: "Custom resumes and cover letters can be previewed before sending; submitted materials are visible.",
        lifeShack: "Optional tailored materials connected to applications and tracking.",
      },
      {
        label: "Special filters",
        competitor: "Includes a visa-sponsorship company filter.",
        lifeShack: "Includes job preferences and company blocklists.",
      },
      {
        label: "Pricing clarity",
        competitor: "No public plan price on the homepage.",
        lifeShack: "Free application credits are available without a card.",
      },
    ],
    sections: [
      {
        heading: "How Massive Works",
        body: [
          "Massive builds a match feed from a candidate profile. Candidates can choose jobs themselves or enable Autopilot. Its FAQ advertises resume and cover-letter previews before sending, plus visibility into full submitted applications. A document preview is not evidence that every application requires approval.",
          "Its public page also highlights hand-vetted companies and a visa-sponsorship filter, making its own job inventory a meaningful part of the experience.",
        ],
      },
      {
        heading: "Where LifeShack Differs",
        body: [
          "LifeShack leads with a free-credit starting path and controls for an ongoing preference-led pipeline. Massive publicly explains the workflow and a ceiling of 200 applications a month but does not show the plan price on its homepage.",
          "Candidates should compare actual role coverage, how each service interprets preferences, the submitted materials, and the price shown in each account flow before committing.",
        ],
      },
      {
        heading: "Bottom Line",
        body: [
          "Use Massive if its job inventory, visa filter, and custom-material workflow suit your search.",
          "Use LifeShack if you want transparent free starting access and a focused set of application controls.",
        ],
      },
    ],
    chooseCompetitor: [
      "You want to search within Massive's matched job inventory.",
      "You need a visa-sponsorship company filter.",
      "You want document previews before sending and visibility into submitted applications.",
    ],
    chooseLifeShack: [
      "You want to start with free application credits.",
      "You value pause controls and company blocklists.",
      "You want a focused preference-led application pipeline.",
    ],
    faqs: [
      {
        question: "Does Massive apply to jobs automatically?",
        answer:
          "Yes. Massive says candidates can turn on Autopilot or manually choose jobs for the service to apply to.",
      },
      {
        question: "How many jobs can Massive apply to?",
        answer:
          "Massive's public homepage advertises up to 200 applications per month, but the current plan price is not displayed there.",
      },
    ],
    sources: [
      { label: "Massive official product page", href: "https://usemassive.com/" },
    ],
  },
  {
    slug: "applygenie",
    name: "ApplyGenie",
    category: "Review-before-submit autofill for LinkedIn Easy Apply and Lever",
    title: "LifeShack vs ApplyGenie",
    metaTitle: "LifeShack vs ApplyGenie | Autofill vs Auto Apply",
    metaDescription:
      "Compare LifeShack and ApplyGenie for LinkedIn and Lever autofill, AI answers, review controls, automatic submission, supported sites, and pricing.",
    officialUrl: "https://applygenie.ai/",
    pricingNote:
      "ApplyGenie lists one-time packs at $9 for 25 supported applications, $29 for 100, and $69 for 400 after a three-day no-card trial.",
    bestFor: [
      "Candidates who apply mainly through LinkedIn Easy Apply and Lever",
      "Applicants who want to review every answer and click submit themselves",
      "Job seekers who prefer one-time application packs to a subscription",
    ],
    watchouts: [
      "Current support is limited to LinkedIn Easy Apply and Lever",
      "The user still opens, reviews, and submits every application",
      "It is an autofill assistant rather than a job-discovery or hands-off agent",
    ],
    verdict:
      "Choose ApplyGenie for low-cost, review-before-submit autofill on LinkedIn Easy Apply and Lever. Choose LifeShack when you want the application workflow to continue without opening every form.",
    competitorSummary:
      "ApplyGenie is a browser extension that fills supported LinkedIn Easy Apply and Lever forms from a saved profile and AI-assisted answers while leaving final review and submission to the candidate.",
    lifeShackSummary:
      "LifeShack is designed to find relevant jobs and keep supported applications moving after setup, with optional tailoring, tracking, pausing, and company blocklists.",
    tableRows: [
      {
        label: "Primary workflow",
        competitor: "Open a supported form, autofill it, review the draft, and submit.",
        lifeShack: "Set preferences and delegate more of the recurring application loop.",
      },
      {
        label: "Supported sites",
        competitor: "LinkedIn Easy Apply and Lever are currently named.",
        lifeShack: "Supported job boards and company application workflows.",
      },
      {
        label: "Final submission",
        competitor: "The candidate reviews and clicks submit.",
        lifeShack: "The agent submits supported applications after setup.",
      },
      {
        label: "Pricing model",
        competitor: "One-time packs of 25, 100, or 400 applications.",
        lifeShack: "Free starting credits and monthly plans for higher volume.",
      },
      {
        label: "Best fit",
        competitor: "Manual control with faster forms.",
        lifeShack: "Delegated application execution.",
      },
    ],
    sections: [
      {
        heading: "How ApplyGenie Works",
        body: [
          "ApplyGenie stores a candidate's skills, work history, preferences, and recurring answers, then uses a Chrome extension to fill supported LinkedIn Easy Apply and Lever forms.",
          "It explicitly keeps the candidate in control: the user reviews the completed form and makes the final submission decision.",
        ],
      },
      {
        heading: "Where LifeShack Differs",
        body: [
          "LifeShack is for candidates who want more than faster typing. After preferences are configured, the product is designed to keep discovering and submitting supported applications without requiring the candidate to open every form.",
          "ApplyGenie's one-time packs and limited supported-site list make the trade-off clear: inexpensive, tightly scoped autofill versus a broader delegated application workflow.",
        ],
      },
      {
        heading: "Bottom Line",
        body: [
          "Use ApplyGenie if LinkedIn Easy Apply and Lever cover most of your search and you want final review control.",
          "Use LifeShack if your main goal is to stop driving each individual application.",
        ],
      },
    ],
    chooseCompetitor: [
      "Most of your applications are on LinkedIn Easy Apply or Lever.",
      "You want to approve every answer and final submission.",
      "You prefer a one-time application pack.",
    ],
    chooseLifeShack: [
      "You want applications moving without opening every form.",
      "You need broader job discovery and workflow coverage.",
      "You want tracking and controls tied to a recurring agent.",
    ],
    faqs: [
      {
        question: "Does ApplyGenie submit applications automatically?",
        answer:
          "No. ApplyGenie says it autofills supported forms and drafts answers, but the candidate reviews and makes the final submission decision.",
      },
      {
        question: "Which job sites does ApplyGenie support?",
        answer:
          "Its official site currently names LinkedIn Easy Apply and Lever, with Indeed described as a future target.",
      },
    ],
    sources: [
      { label: "ApplyGenie official product and pricing page", href: "https://applygenie.ai/" },
    ],
  },
];
